import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { getQuestionnaireAnswers } from "@medplum/core";
import { QuestionnaireResponse, QuestionnaireResponseItemAnswer } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import {
  PostpartumEncounterQuestionnaireURLs,
  PostpartumMedicationsTherapiesQuestionsLinkIds,
} from "@/encounters/constants/postpartum-encounter";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";
import { getFormsetAnswers } from "@/utils/questionnaires";

const MedicationsAndTherapiesDiff = ({ currentVersionIndex }: { currentVersionIndex: number }) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        const [foundAmendmentHistory] = await fetchAmendmentHistoryByQuestionnaireUrl(
          medplum,
          PostpartumEncounterQuestionnaireURLs.MedicationsTherapies,
          encounterId!,
        );

        if (!foundAmendmentHistory) return;

        setCurrentVersion(foundAmendmentHistory[currentVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentVersionIndex + 1]);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [currentVersionIndex, encounterId, medplum]);

  if (isFetchingInitialData) return <Loading />;

  // If no medications and therapies data has ever been recorded
  if (!currentVersion) {
    return (
      <Paper p="md" withBorder>
        <Stack mb="lg">
          <Title order={2}>Medications & Therapies</Title>
        </Stack>
        <TextWrapper py="lg" c="gray.6" ta="center">
          No medications and therapies data has been recorded for this encounter.
        </TextWrapper>
      </Paper>
    );
  }

  // If medications and therapies data exists but no previous version to compare against,
  // create an empty previous version to reuse the existing diff logic
  const effectivePreviousVersion =
    previousVersion || ({ item: [] } as unknown as QuestionnaireResponse);

  // Helper function to extract individual medication fields
  const extractMedicationField = (
    questionnaireResponse: QuestionnaireResponse,
    fieldName: string,
    medicationIndex: number,
  ): string => {
    const medicationsFormset = getFormsetAnswers(
      questionnaireResponse,
      PostpartumMedicationsTherapiesQuestionsLinkIds.MedicationFormset,
      PostpartumMedicationsTherapiesQuestionsLinkIds.MedicationFormsetCode,
    );

    const medications = Object.values(medicationsFormset);
    if (medications.length === 0 || !medications[medicationIndex]) return "";

    const medication = medications[medicationIndex][0];
    if (!medication) return "";

    switch (fieldName) {
      case "medication":
        return (
          medication?.postpartumMedicationAndTherapiesMedicationsFormsetMedication?.valueCoding
            ?.display || `Medication ${medicationIndex + 1}`
        );
      case "form":
        return (
          medication?.postpartumMedicationAndTherapiesMedicationsFormsetForm?.valueCoding
            ?.display || ""
        );
      case "dosage":
        return (
          (
            medication?.postpartumMedicationAndTherapiesMedicationFormDosage as Record<
              string,
              QuestionnaireResponseItemAnswer
            >
          )?.postpartumMedicationAndTherapiesMedicationsFormsetDosage?.valueCoding?.display || ""
        );
      case "route":
        return (
          (
            medication?.postpartumMedicationAndTherapiesMedicationFormDosage as Record<
              string,
              QuestionnaireResponseItemAnswer
            >
          )?.postpartumMedicationAndTherapiesMedicationsFormsetRoute?.valueCoding?.display || ""
        );
      case "frequency":
        return (
          (
            medication?.postpartumMedicationAndTherapiesMedicationsFrequencyDuration as Record<
              string,
              QuestionnaireResponseItemAnswer
            >
          )?.postpartumMedicationAndTherapiesMedicationsFormsetFrequency?.valueCoding?.display || ""
        );
      case "duration":
        return (
          (
            medication?.postpartumMedicationAndTherapiesMedicationsFrequencyDuration as Record<
              string,
              QuestionnaireResponseItemAnswer
            >
          )?.postpartumMedicationAndTherapiesMedicationsFormsetDuration?.valueCoding?.display || ""
        );
      case "days":
        return (
          (
            medication?.postpartumMedicationAndTherapiesMedicationsFrequencyDuration as Record<
              string,
              QuestionnaireResponseItemAnswer
            >
          )?.postpartumMedicationAndTherapiesMedicationsFrequencyDurationXDays?.valueDecimal?.toString() ||
          ""
        );
      case "specialInstructions":
        return (
          medication?.postpartumMedicationAndTherapiesMedicationsFormsetSpecialInstructions
            ?.valueCoding?.display || ""
        );
      case "prnReason":
        return (
          medication?.postpartumMedicationAndTherapiesMedicationsFormsetPRNReason?.valueCoding
            ?.display || ""
        );
      default:
        return "";
    }
  };

  // Helper function to extract individual therapy fields
  const extractTherapyField = (
    questionnaireResponse: QuestionnaireResponse,
    fieldName: string,
    therapyIndex: number,
  ): string => {
    const therapiesFormset = getFormsetAnswers(
      questionnaireResponse,
      PostpartumMedicationsTherapiesQuestionsLinkIds.TherapiesFormset,
      PostpartumMedicationsTherapiesQuestionsLinkIds.TherapiesFormsetCode,
    );

    const therapies = Object.values(therapiesFormset);
    if (therapies.length === 0 || !therapies[therapyIndex]) return "";

    const therapy = therapies[therapyIndex][0];
    if (!therapy) return "";

    switch (fieldName) {
      case "therapy":
        return (
          therapy?.postpartumMedicationAndTherapiesTherapiesFormsetTherapy?.valueString ||
          `Therapy ${therapyIndex + 1}`
        );
      case "reason":
        return therapy?.postpartumMedicationAndTherapiesTherapiesReason?.valueCoding?.display || "";
      case "otherReason":
        return therapy?.postpartumMedicationAndTherapiesTherapiesReasonOther?.valueString || "";
      default:
        return "";
    }
  };

  // Helper function to get the count of medications
  const getMedicationCount = (questionnaireResponse: QuestionnaireResponse): number => {
    const medicationsFormset = getFormsetAnswers(
      questionnaireResponse,
      PostpartumMedicationsTherapiesQuestionsLinkIds.MedicationFormset,
      PostpartumMedicationsTherapiesQuestionsLinkIds.MedicationFormsetCode,
    );
    return Object.values(medicationsFormset).length;
  };

  // Helper function to get the count of therapies
  const getTherapyCount = (questionnaireResponse: QuestionnaireResponse): number => {
    const therapiesFormset = getFormsetAnswers(
      questionnaireResponse,
      PostpartumMedicationsTherapiesQuestionsLinkIds.TherapiesFormset,
      PostpartumMedicationsTherapiesQuestionsLinkIds.TherapiesFormsetCode,
    );
    return Object.values(therapiesFormset).length;
  };

  // Helper function to get notes
  const getNotes = (questionnaireResponse: QuestionnaireResponse): string => {
    const answers = getQuestionnaireAnswers(questionnaireResponse);
    return answers[PostpartumMedicationsTherapiesQuestionsLinkIds.Notes]?.valueString || "";
  };

  // Get counts for medications and therapies
  const previousMedicationCount = getMedicationCount(effectivePreviousVersion);
  const currentMedicationCount = getMedicationCount(currentVersion);
  const maxMedicationCount = Math.max(previousMedicationCount, currentMedicationCount);

  const previousTherapyCount = getTherapyCount(effectivePreviousVersion);
  const currentTherapyCount = getTherapyCount(currentVersion);
  const maxTherapyCount = Math.max(previousTherapyCount, currentTherapyCount);

  const previousNotes = getNotes(effectivePreviousVersion);
  const currentNotes = getNotes(currentVersion);

  // Check if both versions are completely empty
  const isCompletelyEmpty =
    maxMedicationCount === 0 && maxTherapyCount === 0 && !previousNotes && !currentNotes;

  if (isCompletelyEmpty) {
    return (
      <Paper p="md" withBorder>
        <Stack mb="lg">
          <Title order={2}>Medications & Therapies</Title>
        </Stack>
        <TextWrapper py="lg" c="gray.6" ta="center">
          No medications and therapies data has been recorded for this encounter.
        </TextWrapper>
      </Paper>
    );
  }

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Medications & Therapies</Title>
      </Stack>
      <Group grow align="stretch">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="md">
              {/* Medications Section */}
              {maxMedicationCount > 0 && (
                <Stack gap="xs">
                  <TextWrapper fw={600}>Medications</TextWrapper>
                  {Array.from({ length: maxMedicationCount }, (_, index) => (
                    <Stack ml="md" gap="xs" key={index}>
                      <Stack gap="0">
                        <TextWrapper fw={600}>Medication {index + 1}</TextWrapper>
                        <WordsDiff
                          showRemoved
                          current={extractMedicationField(currentVersion, "medication", index)}
                          previous={extractMedicationField(
                            effectivePreviousVersion,
                            "medication",
                            index,
                          )}
                        />
                      </Stack>
                      <Stack gap="0">
                        <TextWrapper fw={600}>Form</TextWrapper>
                        <WordsDiff
                          showRemoved
                          current={extractMedicationField(currentVersion, "form", index)}
                          previous={extractMedicationField(effectivePreviousVersion, "form", index)}
                        />
                      </Stack>
                      <Stack gap="0">
                        <TextWrapper fw={600}>Dosage</TextWrapper>
                        <WordsDiff
                          showRemoved
                          current={extractMedicationField(currentVersion, "dosage", index)}
                          previous={extractMedicationField(
                            effectivePreviousVersion,
                            "dosage",
                            index,
                          )}
                        />
                      </Stack>
                      <Stack gap="0">
                        <TextWrapper fw={600}>Route</TextWrapper>
                        <WordsDiff
                          showRemoved
                          current={extractMedicationField(currentVersion, "route", index)}
                          previous={extractMedicationField(
                            effectivePreviousVersion,
                            "route",
                            index,
                          )}
                        />
                      </Stack>
                      <Stack gap="0">
                        <TextWrapper fw={600}>Frequency</TextWrapper>
                        <WordsDiff
                          showRemoved
                          current={extractMedicationField(currentVersion, "frequency", index)}
                          previous={extractMedicationField(
                            effectivePreviousVersion,
                            "frequency",
                            index,
                          )}
                        />
                      </Stack>
                      <Stack gap="0">
                        <TextWrapper fw={600}>Duration</TextWrapper>
                        <WordsDiff
                          showRemoved
                          current={extractMedicationField(currentVersion, "duration", index)}
                          previous={extractMedicationField(
                            effectivePreviousVersion,
                            "duration",
                            index,
                          )}
                        />
                      </Stack>
                      <Stack gap="0">
                        <TextWrapper fw={600}># of Days</TextWrapper>
                        <WordsDiff
                          showRemoved
                          current={extractMedicationField(currentVersion, "days", index)}
                          previous={extractMedicationField(effectivePreviousVersion, "days", index)}
                        />
                      </Stack>
                      <Stack gap="0">
                        <TextWrapper fw={600}>Special Instructions</TextWrapper>
                        <WordsDiff
                          showRemoved
                          current={extractMedicationField(
                            currentVersion,
                            "specialInstructions",
                            index,
                          )}
                          previous={extractMedicationField(
                            effectivePreviousVersion,
                            "specialInstructions",
                            index,
                          )}
                        />
                      </Stack>
                      <Stack gap="0">
                        <TextWrapper fw={600}>PRN Reason</TextWrapper>
                        <WordsDiff
                          showRemoved
                          current={extractMedicationField(currentVersion, "prnReason", index)}
                          previous={extractMedicationField(
                            effectivePreviousVersion,
                            "prnReason",
                            index,
                          )}
                        />
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              )}

              {/* Therapies Section */}
              {maxTherapyCount > 0 && (
                <Stack gap="xs">
                  <TextWrapper fw={600}>Complementary Therapies</TextWrapper>
                  {Array.from({ length: maxTherapyCount }, (_, index) => (
                    <Stack ml="md" gap="xs" key={index}>
                      <Stack gap="0">
                        <TextWrapper fw={600}>Therapy {index + 1}</TextWrapper>
                        <WordsDiff
                          showRemoved
                          current={extractTherapyField(currentVersion, "therapy", index)}
                          previous={extractTherapyField(effectivePreviousVersion, "therapy", index)}
                        />
                      </Stack>
                      <Stack gap="0">
                        <TextWrapper fw={600}>Reason</TextWrapper>
                        <WordsDiff
                          showRemoved
                          current={extractTherapyField(currentVersion, "reason", index)}
                          previous={extractTherapyField(effectivePreviousVersion, "reason", index)}
                        />
                      </Stack>
                      <Stack gap="0">
                        <TextWrapper fw={600}>Other Reason</TextWrapper>
                        <WordsDiff
                          showRemoved
                          current={extractTherapyField(currentVersion, "otherReason", index)}
                          previous={extractTherapyField(
                            effectivePreviousVersion,
                            "otherReason",
                            index,
                          )}
                        />
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              )}

              {/* Notes Section */}
              <Stack gap="0">
                <TextWrapper fw={600}>Notes</TextWrapper>
                <WordsDiff showRemoved current={currentNotes} previous={previousNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="md">
              {/* Medications Section */}
              {maxMedicationCount > 0 && (
                <Stack gap="xs">
                  <TextWrapper fw={600}>Medications</TextWrapper>
                  {Array.from({ length: maxMedicationCount }, (_, index) => (
                    <Stack ml="md" gap="xs" key={index}>
                      <Stack gap="0">
                        <TextWrapper fw={600}>Medication {index + 1}</TextWrapper>
                        <WordsDiff
                          current={extractMedicationField(currentVersion, "medication", index)}
                          previous={extractMedicationField(
                            effectivePreviousVersion,
                            "medication",
                            index,
                          )}
                        />
                      </Stack>
                      <Stack gap="0">
                        <TextWrapper fw={600}>Form</TextWrapper>
                        <WordsDiff
                          current={extractMedicationField(currentVersion, "form", index)}
                          previous={extractMedicationField(effectivePreviousVersion, "form", index)}
                        />
                      </Stack>
                      <Stack gap="0">
                        <TextWrapper fw={600}>Dosage</TextWrapper>
                        <WordsDiff
                          current={extractMedicationField(currentVersion, "dosage", index)}
                          previous={extractMedicationField(
                            effectivePreviousVersion,
                            "dosage",
                            index,
                          )}
                        />
                      </Stack>
                      <Stack gap="0">
                        <TextWrapper fw={600}>Route</TextWrapper>
                        <WordsDiff
                          current={extractMedicationField(currentVersion, "route", index)}
                          previous={extractMedicationField(
                            effectivePreviousVersion,
                            "route",
                            index,
                          )}
                        />
                      </Stack>
                      <Stack gap="0">
                        <TextWrapper fw={600}>Frequency</TextWrapper>
                        <WordsDiff
                          current={extractMedicationField(currentVersion, "frequency", index)}
                          previous={extractMedicationField(
                            effectivePreviousVersion,
                            "frequency",
                            index,
                          )}
                        />
                      </Stack>
                      <Stack gap="0">
                        <TextWrapper fw={600}>Duration</TextWrapper>
                        <WordsDiff
                          current={extractMedicationField(currentVersion, "duration", index)}
                          previous={extractMedicationField(
                            effectivePreviousVersion,
                            "duration",
                            index,
                          )}
                        />
                      </Stack>
                      <Stack gap="0">
                        <TextWrapper fw={600}># of Days</TextWrapper>
                        <WordsDiff
                          current={extractMedicationField(currentVersion, "days", index)}
                          previous={extractMedicationField(effectivePreviousVersion, "days", index)}
                        />
                      </Stack>
                      <Stack gap="0">
                        <TextWrapper fw={600}>Special Instructions</TextWrapper>
                        <WordsDiff
                          current={extractMedicationField(
                            currentVersion,
                            "specialInstructions",
                            index,
                          )}
                          previous={extractMedicationField(
                            effectivePreviousVersion,
                            "specialInstructions",
                            index,
                          )}
                        />
                      </Stack>
                      <Stack gap="0">
                        <TextWrapper fw={600}>PRN Reason</TextWrapper>
                        <WordsDiff
                          current={extractMedicationField(currentVersion, "prnReason", index)}
                          previous={extractMedicationField(
                            effectivePreviousVersion,
                            "prnReason",
                            index,
                          )}
                        />
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              )}

              {/* Therapies Section */}
              {maxTherapyCount > 0 && (
                <Stack gap="xs">
                  <TextWrapper fw={600}>Complementary Therapies</TextWrapper>
                  {Array.from({ length: maxTherapyCount }, (_, index) => (
                    <Stack ml="md" gap="xs" key={index}>
                      <Stack gap="0">
                        <TextWrapper fw={600}>Therapy {index + 1}</TextWrapper>
                        <WordsDiff
                          current={extractTherapyField(currentVersion, "therapy", index)}
                          previous={extractTherapyField(effectivePreviousVersion, "therapy", index)}
                        />
                      </Stack>
                      <Stack gap="0">
                        <TextWrapper fw={600}>Reason</TextWrapper>
                        <WordsDiff
                          current={extractTherapyField(currentVersion, "reason", index)}
                          previous={extractTherapyField(effectivePreviousVersion, "reason", index)}
                        />
                      </Stack>
                      <Stack gap="0">
                        <TextWrapper fw={600}>Other Reason</TextWrapper>
                        <WordsDiff
                          current={extractTherapyField(currentVersion, "otherReason", index)}
                          previous={extractTherapyField(
                            effectivePreviousVersion,
                            "otherReason",
                            index,
                          )}
                        />
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              )}

              {/* Notes Section */}
              <Stack gap="0">
                <TextWrapper fw={600}>Notes</TextWrapper>
                <WordsDiff current={currentNotes} previous={previousNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default MedicationsAndTherapiesDiff;
